"use client";

import { useChatMessages } from "@ai-sdk-tools/store";
import type { UIMessage } from "@ai-sdk/react";
import { VideoCanvas } from "./video-canvas";
import type { VideoArtifactData } from "@/ai/artifacts/video";
import { useEffect, useMemo, useState } from "react";
import { useChatInterface } from "@/hooks/use-chat-interface";
import Image from "next/image";

type LatestArtifact =
  | { kind: "video"; data: VideoArtifactData }
  | null;

function extractLatestArtifact(messages: UIMessage[]): LatestArtifact {
  const isValidVideo = (c: unknown): c is VideoArtifactData =>
    !!c &&
    typeof c === "object" &&
    (c as VideoArtifactData).type === "mindup-video" &&
    typeof (c as VideoArtifactData).url === "string" &&
    typeof (c as VideoArtifactData).title === "string";

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const parts: unknown[] = Array.isArray((msg as { parts?: unknown })?.parts)
      ? (msg as { parts: unknown[] }).parts
      : [];

    for (let j = parts.length - 1; j >= 0; j--) {
      const part = parts[j] as Record<string, unknown>;
      const payload =
        (part?.data ?? part?.payload ?? part?.output ?? part?.result ?? part?.artifact) as
          | Record<string, unknown>
          | null;

      const candidates = [
        payload,
        payload?.payload,
        payload?.data,
        payload?.artifact,
        (payload?.artifact as Record<string, unknown>)?.payload,
        part,
      ].filter(Boolean) as unknown[];

      for (const candidate of candidates) {
        if (isValidVideo(candidate)) {
          return { kind: "video", data: candidate };
        }
      }
    }
  }
  return null;
}

function ArtifactHeader() {
  return (
    <div className="shrink-0 p-5 border-b border-gray-100 dark:border-white/10 bg-gradient-to-r from-[var(--mindup-pink-light)]/40 to-[var(--mindup-mint)]/40 dark:from-[var(--mindup-plum)]/20 dark:to-[var(--mindup-deep-green)]/10">
      <div className="flex items-center gap-2.5 mb-1.5">
        <Image
          src="/brand/avatars/MindUP_Avatars_RGB_BrainAvatar.png"
          alt="MindUP"
          width={32}
          height={32}
          className="w-8 h-8 rounded-full shadow-sm"
        />
        <h2 className="text-base font-bold text-[var(--mindup-soft-black)] dark:text-white">Content</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 ml-10">MindUP resources and media</p>
    </div>
  );
}

export function ArtifactCanvas() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-[var(--mindup-soft-black)]">
        <ArtifactHeader />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[var(--mindup-pink)]/30 border-t-[var(--mindup-pink)] rounded-full animate-spin" />
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return <ArtifactCanvasContent />;
}

function ArtifactCanvasContent() {
  const { chatId } = useChatInterface();
  const messages = useChatMessages() as unknown as UIMessage[];
  const [fetchedMessages, setFetchedMessages] = useState<UIMessage[] | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!chatId) return;
      if (Array.isArray(messages) && messages.length > 0) return;
      try {
        const res = await fetch(`/api/chats/${chatId}/messages`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setFetchedMessages(data as UIMessage[]);
        }
      } catch {
        // ignore
      }
    };
    fetchMessages();
  }, [chatId, messages]);

  const allMessages = useMemo<UIMessage[]>(() => {
    if (Array.isArray(messages) && messages.length > 0) return messages as UIMessage[];
    if (Array.isArray(fetchedMessages)) return fetchedMessages as UIMessage[];
    return [];
  }, [messages, fetchedMessages]);

  const artifact = useMemo(
    () =>
      Array.isArray(allMessages) ? extractLatestArtifact(allMessages) : null,
    [allMessages],
  );

  if (artifact?.kind === "video") {
    return <VideoCanvas data={artifact.data} />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-[var(--mindup-soft-black)] relative">
      <ArtifactHeader />
      <div className="flex-1 overflow-auto p-8 flex flex-col items-center justify-center text-center relative">
        {/* Floating animated brand icons — on colored backgrounds */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-6 right-6 w-8 h-8 rounded-xl bg-[var(--mindup-pink-light)] p-1.5 opacity-50 animate-[float_5s_ease-in-out_infinite]">
            <Image src="/brand/icons/sparkle.gif" alt="" width={20} height={20} className="w-full h-full" unoptimized />
          </div>
          <div className="absolute bottom-16 left-6 w-7 h-7 rounded-lg bg-[var(--mindup-orange-light)] p-1 opacity-40 animate-[float_7s_ease-in-out_infinite_1s]">
            <Image src="/brand/icons/smiley.gif" alt="" width={20} height={20} className="w-full h-full" unoptimized />
          </div>
          <div className="absolute top-[45%] left-4 w-7 h-7 rounded-lg bg-[var(--mindup-mint)] p-1 opacity-35 animate-[float_6s_ease-in-out_infinite_2s]">
            <Image src="/brand/icons/music.gif" alt="" width={20} height={20} className="w-full h-full" unoptimized />
          </div>
        </div>

        {/* Brand-styled empty state */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--mindup-pink-light)]/60 to-[var(--mindup-mint)]/40 rounded-full blur-xl scale-150" />
          <div className="relative w-20 h-20 rounded-2xl bg-white dark:bg-[#363636] shadow-lg flex items-center justify-center border border-gray-100 dark:border-white/10">
            <Image
              src="/brand/icons/star.gif"
              alt=""
              width={44}
              height={44}
              className="w-11 h-11"
              unoptimized
            />
          </div>
        </div>
        <h3 className="font-display text-xl text-[var(--mindup-soft-black)] dark:text-white mb-2 leading-[1]">
          Ready to Create
        </h3>
        <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
          Ask about MindUP lessons, activities, or mindfulness practices—detailed responses appear in the chat.
        </p>
      </div>
    </div>
  );
}
